import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChurchIcon } from '../components/common/Icons';
import { checkRequired, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #e0e7ff 0%, #dbeafe 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: 'clamp(20px, 6vw, 40px)',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  logoIcon: {
    fontSize: '3.5rem',
    display: 'block',
    marginBottom: '8px',
  },
  logoTitle: {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: '#1e40af',
    marginBottom: '4px',
  },
  logoSubtitle: {
    fontSize: '0.9rem',
    color: '#64748b',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    color: '#1e293b',
  },
  inputFocus: {
    borderColor: '#1e40af',
    boxShadow: '0 0 0 3px rgba(30,64,175,0.15)',
  },
  button: {
    backgroundColor: '#1e40af',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background 0.2s',
  },
  buttonHover: {
    backgroundColor: '#1e3a8a',
  },
  buttonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px',
    color: '#b91c1c',
    fontSize: '0.9rem',
    textAlign: 'center',
  },
  footer: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '0.8rem',
    color: '#94a3b8',
  },
};

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [btnHover, setBtnHover] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = checkRequired({ username, password }, { username: "Nom d'utilisateur", password: 'Mot de passe' });
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setLoading(true);
    setError('');
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Identifiants incorrects. Veuillez réessayer.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (!navigator.onLine) {
        setError('Connexion impossible. Vérifiez votre connexion réseau.');
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Bouton retour */}
      <a href="/" style={{ position: 'fixed', top: '20px', left: '20px', display: 'flex', alignItems: 'center', gap: '6px', color: '#1e40af', textDecoration: 'none', fontSize: '0.82rem', fontWeight: 600, background: 'rgba(255,255,255,0.8)', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '8px 14px', backdropFilter: 'blur(8px)', transition: 'background 0.15s', zIndex: 10 }}
        onMouseEnter={e => e.currentTarget.style.background = 'white'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.8)'}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        Accueil
      </a>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}><ChurchIcon size={56} color="#1e40af" /></span>
          <div style={styles.logoTitle}>RCC</div>
          <div style={styles.logoSubtitle}>Gestion des présences aux activités</div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          {error && <div style={styles.error}>{error}</div>}

          <div>
            <label style={styles.label} htmlFor="username">
              Nom d'utilisateur<Req />
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Entrez votre identifiant"
              style={{
                ...styles.input,
                ...(focusedField === 'username' ? styles.inputFocus : {}),
                ...(fieldErrors.username ? { borderColor: ERROR_COLOR } : {}),
              }}
              onFocus={() => setFocusedField('username')}
              onBlur={() => setFocusedField(null)}
              disabled={loading}
            />
            <FieldError message={fieldErrors.username} />
          </div>

          <div>
            <label style={styles.label} htmlFor="password">
              Mot de passe<Req />
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Entrez votre mot de passe"
              style={{
                ...styles.input,
                ...(focusedField === 'password' ? styles.inputFocus : {}),
                ...(fieldErrors.password ? { borderColor: ERROR_COLOR } : {}),
              }}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              disabled={loading}
            />
            <FieldError message={fieldErrors.password} />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : btnHover ? styles.buttonHover : {}),
            }}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
          >
            {loading ? 'Connexion en cours...' : 'Se connecter'}
          </button>
        </form>

        <div style={styles.footer}>
          Église — Application interne &copy; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
